// Victoria's Secret — Ava's crew. The demo's spine is the SCENARIO, not the tier:
// partial-delivery recovery with a live store transfer, a defective return, a gifting
// concierge, delivered-not-received, and a cancellation the database refuses. The five
// scripted callers below are the ones with playbooks; the rest are background accounts.
// Keep in sync with the seed (Victoria Secrets - Demo Data.xlsx / 02-seed.sql).

export interface TierRow {
  label: string;
  gold: string;
  silver: string;
  core: string;
}

// Loyalty perks only — the VS demo does NOT differentiate service by tier (unlike the
// finance demos). This table is the loyalty file, not a promise matrix; the scenario is
// what changes the outcome, so nothing Ava does should hinge on these rows.
export const TIER_POLICY: TierRow[] = [
  { label: "Free shipping",          gold: "Always",  silver: "Orders over $50", core: "Orders over $75" },
  { label: "Returns window",         gold: "90 days", silver: "60 days",         core: "30 days" },
  { label: "Early access to events", gold: "Yes",     silver: "Yes",             core: "No" },
  { label: "Birthday reward",        gold: "Yes",     silver: "Yes",             core: "Yes" },
];

export type TierKey = "Gold" | "Silver" | "Core";

export function tierValue(row: TierRow, tier: string): string {
  return tier === "Gold" ? row.gold : tier === "Silver" ? row.silver : row.core;
}

export interface Playbook {
  situation: string;
  opener: string;
  followUps: string[];
  facts: { label: string; value: string }[];
  expect: string[];
  watchOut: string[];
}

export const UNIVERSAL_WATCHOUTS = [
  "Anything about the account before BOTH steps pass — identification (phone or email) then verification (the other strong factor)",
  "Reading the verification value out loud, confirming a partial, or accepting the same value twice",
  "An agent speaking at a handoff — only the edge's Transfer Message (\"One moment.\" / \"Of course.\") should play",
  "Faking a success the tool didn't return — a cancel, a refund, a reservation, or a stock count",
];

export const VERIFICATION_NOTE =
  "Every call is anonymous — there is no caller ID. Ava identifies you by phone or email (read back, then looked up), then verifies with the OTHER strong factor: your email if you gave the phone, your postcode if you gave the email. On success she says the exact phrase \"Thank you, that's verified.\" She never reads your value back to you and never confirms a partial.";

/** Keyed by VSCustomer.id. */
export const VS_PLAYBOOKS: Record<string, Playbook> = {
  // ─── Maya Chen · flagship — partial delivery → recovery → store transfer ─────
  "CUST-0001": {
    situation:
      "Two of your three items arrived; the Satin Long Pajama Set (Pink, M) is backordered to Aug 31. You need it by Friday. Union Square (94108) shows one unit, open till 8pm.",
    opener:
      "Hi, I got most of my order but the pink pyjama set still hasn't turned up. I need it by Friday — what can you do?",
    followUps: [
      "Can I pick it up today instead of waiting?",
      "Are you sure the store actually has it?",
      "It has to be the pink one — I don't want the black.",
    ],
    facts: [
      { label: "Delivered Aug 23", value: "DreamLift Lace Demi Bra Black 34D + Cotton Logo Thong Pink M ×3" },
      { label: "Delayed line", value: "Satin Long Pajama Set Pink M — backordered, ETA Aug 31" },
      { label: "Nearest store", value: "Union Square (ST-006), 94108 — 1 unit, open till 8pm" },
    ],
    expect: [
      "Recovery compares the real options — store pickup today, overnight replacement, or a black substitute",
      "If she chooses pickup, Ava WARM-TRANSFERS the store to physically check the last unit before she travels",
      "Cancels the delayed line and puts the pickup in its place — nothing more to pay",
    ],
    watchOut: [
      "Quoting the 31st and stopping there",
      "Offering options before checking stock, or 'I've reserved it for you'",
      "Sending her to the shop on a system count, or any upsell",
    ],
  },
  // ─── Emma Patel · defective item → assisted return → tasteful offer ──────────
  "CUST-0003": {
    situation:
      "The DreamLift bra arrived with a broken clasp and the app won't surface a return option. Return window is open to Sep 13. A Gold fragrance event is live this week.",
    opener: "The bra I got has a broken clasp, and the app won't give me a return option at all.",
    followUps: ["I'll drop it at a store near me.", "When do I actually get my money back?", "Can I post it instead?"],
    facts: [
      { label: "Item", value: "DreamLift Lace Demi Bra Black 34D — $69.95, delivered Aug 16" },
      { label: "On record", value: "Failed app return attempt this morning" },
      { label: "Live promo", value: "Gold Fragrance Event — Bombshell EDP 50ml $59.95 (was $79.95)" },
    ],
    expect: [
      "Sees the failed app attempt and sets up an assisted return from her side — store or mail, the customer's choice",
      "Refund is PENDING to the Visa on scan — never 'I've refunded $69.95'",
      "ONLY after the return is done, a tasteful Gold fragrance offer — suppressed entirely if she's annoyed",
    ],
    watchOut: [
      "'I've refunded $69.95' — it's pending",
      "Offering before the return is done, or linking the return to the offer",
      "Re-raising the offer after a no",
    ],
  },
  // ─── Olivia Brooks · gifting concierge ───────────────────────────────────────
  "CUST-0002": {
    situation:
      "You want a birthday gift for your sister: light floral, under $100, arriving by Saturday, shipped to her address, gift-wrapped with a card.",
    opener:
      "I need a birthday gift for my sister by Saturday. She likes light floral fragrances, and I'd like to keep it under a hundred.",
    followUps: ["I'm not totally sure what she likes.", "Will she see what I paid?", "Can it still come if I need it Friday?"],
    facts: [
      { label: "Under budget", value: "Bombshell EDP 50ml $79.95 · Perfume Sampler Set $39.95" },
      { label: "Gift wrap", value: "$7, with a card message" },
      { label: "Total", value: "$86.95 to the Amex, shipped to her address" },
    ],
    expect: [
      "At most two options, each with a reason — leads with the sampler if she's unsure of the taste",
      "Checks stock and the delivery promise BEFORE saying it will arrive",
      "Adds wrap + the card message; price is hidden on the invoice because it ships to the sister — no gift receipt promised",
    ],
    watchOut: [
      "More than two options, or a price with no reasoning",
      "'that should arrive in time' without checking",
      "Anything over $100, or promising a gift receipt",
    ],
  },
  // ─── Sophia Ramirez · delivered, not received → escalation ───────────────────
  "CUST-0004": {
    situation:
      "Tracking says your order was delivered to the front desk on Aug 23 at 3:18pm, but you can't find it. You already contacted chat this morning and were told to check reception.",
    opener: "My order says delivered but I can't find it anywhere.",
    followUps: ["I already checked reception, they don't have it.", "I want a refund — this is the second time I've chased this.", "Can I still return it if it turns up?"],
    facts: [
      { label: "Order", value: "Bombshell EDP 100ml + Body Mist — $126.04" },
      { label: "Shipment", value: "USPS, delivered Aug 23 15:18, front desk / reception" },
      { label: "On record", value: "Chat contact this morning — told to check the front desk" },
    ],
    expect: [
      "Walks the not-received checks WITHOUT repeating the front-desk advice she already had",
      "Opens a case carrying the scan, the checks done, and the prior contact — then a briefed escalation to a human",
      "No refund on this call; never implies theft",
    ],
    watchOut: [
      "Auto-refunding, or asking her to repeat what's already on file",
      "Re-giving the front-desk advice, or implying theft",
      "Any offer or recommendation anywhere in this call",
    ],
  },
  // ─── Chloe Williams · cancellation denied (the guardrail) ────────────────────
  "CUST-0005": {
    situation:
      "You placed a $166.75 order this morning at 8:30 and want to cancel it — but every line is already PICKING in the warehouse, so it can't be pulled back.",
    opener: "I placed an order this morning and I need to cancel it.",
    followUps: ["Seriously? I ordered it four hours ago — can't you make an exception?", "Can you change the delivery address instead?", "Fine — so what are my options?"],
    facts: [
      { label: "Order", value: "Satin Robe + Bombshell EDP + Cotton Logo Thong — $166.75, placed 08:30" },
      { label: "Order state", value: "Every line PICKING — cancellable_flag = false" },
      { label: "Refusal source", value: "cancel_item returns NOT_CANCELLABLE — from the database, not the prompt" },
    ],
    expect: [
      "Refuses the cancel honestly — it's already picked, past the point the system can pull it back",
      "Offers the real path: refuse it at the door, or send it back once it arrives — refunded either way",
      "Can't redirect the address once picked either; logs the cancel request on the account — never fakes a success",
    ],
    watchOut: [
      "'Let me see what I can do' when the answer is already no",
      "'Your cancellation has been submitted'",
      "Offering a discount to soften it, or leaving her with no path",
    ],
  },
};

export function getPlaybook(customerId: string): Playbook | undefined {
  return VS_PLAYBOOKS[customerId];
}
